import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SupportMessageBody } from '@/components/support/SupportMessageBody';
import {
  getSupportMessageAttachmentName,
  hasSupportMessageAttachment,
  isSupportMessageImageAttachment,
  type SupportMessageLike,
} from '@/lib/supportChat';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { AlertCircle, CheckCheck, Clock3, FileText, ImageIcon, RefreshCw } from 'lucide-react';

interface SupportMessageListProps {
  messages: SupportMessageLike[];
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  onMediaLoad?: () => void;
  currentUserSenderType?: SupportMessageLike['sender_type'];
  statusSenderType?: SupportMessageLike['sender_type'] | null;
  currentUserLabel?: string;
  otherUserLabel?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

function formatMessageTimestamp(value: string) {
  return format(new Date(value), 'MMM d, h:mm a');
}

function renderStatus(message: SupportMessageLike, statusSenderType: SupportMessageLike['sender_type'] | null) {
  if (!statusSenderType || message.sender_type !== statusSenderType) {
    return null;
  }

  if (message.delivery_status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-destructive">
        <AlertCircle className="h-3 w-3" />
        Failed
      </span>
    );
  }

  if (message.delivery_status === 'delivered') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
        <CheckCheck className="h-3 w-3" />
        Delivered
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
      <Clock3 className="h-3 w-3" />
      {message.delivery_status === 'sent' ? 'Sent' : 'Sending'}
    </span>
  );
}

export function SupportMessageList({
  messages,
  loading,
  error,
  onRetry,
  onMediaLoad,
  currentUserSenderType = 'user',
  statusSenderType = 'user',
  currentUserLabel,
  otherUserLabel,
  emptyTitle = 'Start a WhatsApp conversation with support.',
  emptyDescription = 'Your messages here notify our support team on WhatsApp, and they reply back in this inbox.',
}: SupportMessageListProps) {

  if (loading) {
    return (
      <div className="space-y-3 px-1 py-2">
        <Skeleton className="h-16 w-[82%] rounded-2xl" />
        <Skeleton className="ml-auto h-16 w-[74%] rounded-2xl" />
        <Skeleton className="h-20 w-[68%] rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-destructive/30 bg-destructive/5 p-6 text-center">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <div>
          <p className="font-medium text-foreground">We couldn’t load your support thread.</p>
          <p className="text-sm text-muted-foreground">Please try again in a moment.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onRetry} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/70 bg-muted/20 p-6 text-center">
        <div>
          <p className="font-medium text-foreground">{emptyTitle}</p>
          <p className="text-sm text-muted-foreground">{emptyDescription}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 px-1 py-2">
      {messages.map((message) => {
        const isCurrentUserMessage = message.sender_type === currentUserSenderType;
        const senderLabel = isCurrentUserMessage ? currentUserLabel : otherUserLabel;
        const attachmentName = getSupportMessageAttachmentName(message) || 'Attachment';
        const hasAttachment = hasSupportMessageAttachment(message);
        const isImageAttachment = hasAttachment && isSupportMessageImageAttachment(message) && Boolean(message.media_url);
        const hasBody = message.body.trim().length > 0;

        return (
          <div key={message.id} className={cn('flex', isCurrentUserMessage ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[85%] rounded-2xl px-4 py-3 shadow-sm',
                isCurrentUserMessage
                  ? 'rounded-br-md bg-emerald-600 text-white'
                  : 'rounded-bl-md border border-border/70 bg-card text-card-foreground'
              )}
            >
              <div className="space-y-2">
                {senderLabel ? (
                  <p className={cn('text-xs font-semibold', isCurrentUserMessage ? 'text-white/90' : 'text-muted-foreground')}>
                    {senderLabel}
                  </p>
                ) : null}

                {hasBody ? (
                  <SupportMessageBody
                    body={message.body}
                    isCurrentUserMessage={isCurrentUserMessage}
                    onMediaLoad={onMediaLoad}
                  />
                ) : null}

                {isImageAttachment ? (
                  <div className="overflow-hidden rounded-xl border border-black/10 bg-black/5 dark:border-white/10 dark:bg-white/5">
                    <img
                      src={message.media_url || ''}
                      alt={attachmentName}
                      className="max-h-72 w-full object-cover"
                      onLoad={onMediaLoad}
                    />
                  </div>
                ) : null}

                {hasAttachment && message.media_url ? (
                  <a
                    href={message.media_url}
                    target="_blank"
                    rel="noreferrer"
                    className={cn(
                      'inline-flex items-center gap-2 text-xs underline underline-offset-4',
                      isCurrentUserMessage ? 'text-white/90' : 'text-emerald-600 dark:text-emerald-400'
                    )}
                  >
                    {isImageAttachment ? <ImageIcon className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                    {isImageAttachment ? 'Open image' : attachmentName}
                  </a>
                ) : hasAttachment ? (
                  <div className={cn('inline-flex items-center gap-2 text-xs', isCurrentUserMessage ? 'text-white/90' : 'text-muted-foreground')}>
                    <FileText className="h-3.5 w-3.5" />
                    {attachmentName}
                  </div>
                ) : null}

                <div
                  className={cn(
                    'flex flex-wrap items-center gap-2 text-[11px]',
                    isCurrentUserMessage ? 'justify-end text-white/80' : 'text-muted-foreground'
                  )}
                >
                  <span>{formatMessageTimestamp(message.created_at)}</span>
                  {renderStatus(message, statusSenderType)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
